import React, { useCallback, useEffect, useState } from 'react';
import { Flex, Select, Option, Button, Label, Link, Icon } from '@pega/cosmos-react-core';
import { useDispatch, useSelector } from 'react-redux';
import { appState } from '../../interfaces/common';
import { useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fetchErrorLogs } from '../../redux/actions';
import useLocalTranslation from '../../hooks/useLocalTranslation';
import { ErrorDashboardLink } from '../../constants/common';

const connectionTypeOptions: any = {
  all: { id: 'all', primary: 'All' },
  chat: { id: 'web_messaging', primary: 'Web Messaging' },
  facebook: { id: 'messenger', primary: 'Messenger' },
  'twilio-whatsapp': { id: 'whats_app', primary: 'WhatsApp' },
  twilio: { id: 'sms', primary: 'SMS' },
  custom: { id: 'client_channel_api', primary: 'Client Channel API' },
  sdk_mobile: { id: 'sdk_mobile', primary: 'Mobile Messaging SDK' },
};

const dateRangeOptions = [
  {
    id: 'last_hour',
    primary: 'Last hour',
  },
  {
    id: 'last_day',
    primary: 'Last day',
  },
  {
    id: 'last_week',
    primary: 'Last week',
  },
  {
    id: 'last_month',
    primary: 'Last month',
  },
];

const connectionFilterMap: any = {
  'Web Messaging': 'chat',
  Messenger: 'facebook_pm',
  WhatsApp: 'twilio-whatsapp',
  SMS: 'sms',
  'Client Channel API': 'custom',
  'Mobile Messaging SDK': 'sdk_mobile',
};

const connectionTypesMap: any = {
  'Web Messaging': 'chat',
  Messenger: 'facebook',
  WhatsApp: 'twilio-whatsapp',
  SMS: 'twilio',
  'Client Channel API': 'custom',
  'Mobile Messaging SDK': 'sdk_mobile',
};

const StyledButton = styled(Button)(
  () => css`
    height: 32px;
  `,
);

interface SearchComponentProps {
  setPayloadData: Function;
}

const SearchComponent: React.FC<SearchComponentProps> = ({ setPayloadData }) => {
  const { accounts } = useSelector((state: appState) => state);
  const [connectionTypeList, setConnectionTypeList] = useState([]);
  const [connectionType, setConnectionType] = useState('all');
  const translate = useLocalTranslation();

  const connectionDetailsMap = React.useMemo(() => {
    const connectionTypeSet: any = [];

    const mappedAccountNames = accounts.reduce((mappedAccountNames: any, account: any) => {
      mappedAccountNames[account.title] = { connectionId: account.connectionId, source: account.platform };
      connectionTypeSet[account.sdkPlatform || account.platform] = true;
      return mappedAccountNames;
    }, {});

    const connectionTypes: any = Object.keys(connectionTypeSet).reduce((connectionTypes: any, key) => {
      if (connectionTypeOptions[key]) {
        connectionTypes.push(connectionTypeOptions[key]);
      }
      return connectionTypes;
    }, []);

    const dropDownConnectionTypes: any = [{ id: 'all', primary: 'All' }, ...connectionTypes];
    setConnectionTypeList(dropDownConnectionTypes);
    return mappedAccountNames;
  }, [accounts]);

  const handleConnectionTypeChange = (event: any) => {
    const mappedConnectionType = connectionTypesMap[event?.target?.value];
    mappedConnectionType ? setConnectionType(mappedConnectionType) : setConnectionType('all');
  };

  const dispatch = useDispatch();

  const getConnectionNames = useCallback(() => {
    return accounts?.reduce(
      (connectionName: any, account: any) => {
        if (connectionType === 'all') {
          connectionName.push({ id: account.connectionId, primary: account.title });
        } else if (connectionType === (account.sdkPlatform || account.platform)) {
          connectionName.push({ id: account.connectionId, primary: account.title });
        }
        return connectionName;
      },
      [{ id: '0', primary: 'All' }],
    );
  }, [accounts, connectionType]);

  const [connectionNames, setConnectionNames] = useState(getConnectionNames);

  const { register, handleSubmit, reset } = useForm({
    mode: 'all',
    defaultValues: {
      dateRange: dateRangeOptions?.[0]?.primary,
      connectionType: 'All',
      connectionName: 'All',
    },
  });

  useEffect(() => {
    setConnectionNames(getConnectionNames);
  }, [getConnectionNames]);

  const handleSearchClick = useCallback(
    (data?: any) => {
      const endDate = new Date(Date.now()).toISOString();
      let startDate;
      if (data.dateRange === dateRangeOptions?.[1]?.primary) {
        // To filter for last day
        startDate = new Date(new Date().setDate(new Date().getDate() - 1)).toISOString();
      } else if (data.dateRange === dateRangeOptions?.[2]?.primary) {
        // To filter for last week
        startDate = new Date(new Date().setHours(new Date().getHours() - 168)).toISOString();
      } else if (data.dateRange === dateRangeOptions?.[0]?.primary) {
        // To filter for last hour
        startDate = new Date(new Date().setHours(new Date().getHours() - 1)).toISOString();
      } else {
        // Otherwise it fetches for last month
        startDate = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString();
      }
      const payload: any = { type: 'error_log', logGroupName: 'InboundLogs', startDate: startDate, endDate: endDate };
      if (data.connectionName !== 'All') {
        payload.connectionId = connectionDetailsMap[data.connectionName]?.connectionId;
      }
      payload.source = connectionFilterMap[data.connectionType];
      setPayloadData(payload);
      dispatch(fetchErrorLogs(payload));
    },
    [connectionDetailsMap, dispatch, setPayloadData],
  );

  const clearFilter = () => {
    reset();
  };

  return (
    <Flex container={{ direction: 'column', justify: 'between', pad: 3, gap: 2 }}>
      <Label>{translate('dashboard_info_text')}</Label>
      <Link style={{ justifySelf: 'end' }} href={ErrorDashboardLink} variant="link" target="_blank">
        {translate('error_docs')}
        <Icon name="open" style={{ marginLeft: '0.5rem' }} aria-label="open dashboard docs" />
      </Link>
      <Flex container={{ justify: 'between' }}>
        <Flex container={{ justify: 'start', colGap: 4 }}>
          <Select
            name="connectionType"
            label="Connection type"
            onChange={handleConnectionTypeChange}
            ref={register()}
            data-testid="connection-type-filter"
          >
            {connectionTypeList?.map(({ id, primary }) => (
              <Option key={id} id={id}>
                {primary}
              </Option>
            ))}
          </Select>
          <Select name="connectionName" label="Connection name" ref={register()} data-testid="connection-name-filter">
            {connectionNames.map(({ id, primary }: any) => (
              <Option key={id} id={id}>
                {primary}
              </Option>
            ))}
          </Select>
          <Select name="dateRange" label="Date range" ref={register()} data-testid="date-range-filter">
            {dateRangeOptions.map(({ id, primary }: any) => (
              <Option key={id} id={id}>
                {primary}
              </Option>
            ))}
          </Select>
        </Flex>
        <Flex container={{ justify: 'end', colGap: 4, alignItems: 'center' }}>
          <StyledButton variant="secondary" onClick={clearFilter} data-testid="clear-button">
            Clear
          </StyledButton>
          <StyledButton variant="primary" onClick={handleSubmit(handleSearchClick)} data-testid="search-button">
            Search
          </StyledButton>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SearchComponent;
